/* eslint-disable react/forbid-dom-props */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Clock, MapPin, Building2, Zap, X,
  FileText, User, CheckCircle, Hash, Archive, RotateCcw, Search, Columns3, List, ArrowUpDown, AlertTriangle,
} from 'lucide-react';
import { casesApi, petitionsApi } from '../../services/api';
import { format, formatDistanceToNow, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { triggerBlobDownload } from '../../utils/download';
import { formatDate } from '../../utils/masks';
import { useAuthStore } from '../../store/useAuthStore';
import { PERMISSIONS } from '../../rbac/permissions';
import { generationIssueLabel } from './generationIssue';

const COLS = [
  { key: 'ficha_preenchimento',  label: 'Em Preenchimento',     color: 'var(--status-preenchimento)' },
  { key: 'gerando_peticao',      label: 'Gerando',              color: 'var(--status-gerando)' },
  { key: 'aguardando_revisao',   label: 'Aguardando Revisão',   color: 'var(--status-aguardando-revisao)' },
  { key: 'em_revisao',           label: 'Em Revisão',           color: 'var(--status-em-revisao)' },
  { key: 'aguardando_aprovacao', label: 'Aguardando Aprovação', color: 'var(--status-aguardando-aprovacao)' },
  { key: 'aguardando_protocolo', label: 'Pronto p/ Protocolo',  color: 'var(--status-pronto-protocolo)' },
  { key: 'protocolado',          label: 'Protocolado',          color: 'var(--status-protocolado)' },
];

function isStale(caso) {
  const ref = caso.updated_at || caso.created_at;
  if (!ref) return false;
  return differenceInDays(new Date(), new Date(ref)) > 7;
}


function CaseCard({ caso, onClick }) {
  const stale = isStale(caso);
  const isGenerating = caso.status === 'gerando_peticao';
  const issueLabel = generationIssueLabel(caso);
  return (
    <div className={`case-card${isGenerating ? ' case-card-generating' : ''}`} onClick={() => onClick(caso)}>
      <div className="case-card-name">{caso.client_name || 'Sem nome'}</div>
      <div className="case-card-meta">
        <div className="case-card-meta-row">
          <MapPin size={10} /> {caso.comarca || '—'}
        </div>
        <div className="case-card-meta-row">
          <Building2 size={10} />
          {caso.defendant_name
            ? caso.defendant_name.slice(0, 26) + (caso.defendant_name.length > 26 ? '…' : '')
            : '—'}
        </div>
        <div className="case-card-meta-row">
          <Clock size={10} />
          {caso.created_at ? format(new Date(caso.created_at), 'dd/MM/yy', { locale: ptBR }) : '—'}
          {caso.form_completeness != null && caso.form_completeness < 100 && (
            <span className="badge badge-warning" style={{ marginLeft: 'auto', fontSize: '.6rem', padding: '.1rem .3rem' }}>
              {caso.form_completeness}%
            </span>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.25rem', marginTop: '.35rem' }}>
        {caso.status === 'aguardando_revisao' && (
          <span className="badge badge-gold" style={{ fontSize: '.6rem' }}>
            <Zap size={9} /> Rascunho pronto
          </span>
        )}
        {isGenerating && (
          <span className="badge badge-navy pulsing-badge" style={{ fontSize: '.6rem', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
            <span className="spinner-mini" /> Gerando petição...
          </span>
        )}
        {issueLabel && (
          <span className="badge badge-warning" style={{ fontSize: '.6rem', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
            <AlertTriangle size={9} /> {issueLabel}
          </span>
        )}
        {stale && (
          <span className="badge badge-error" style={{ fontSize: '.6rem' }}>
            Parado
          </span>
        )}
      </div>
    </div>
  );
}

function ProtocolModal({ caso, onClose }) {
  const [number, setNumber] = useState('');
  const queryClient = useQueryClient();

  const protocolMutation = useMutation({
    mutationFn: () => casesApi.updateStatus(caso.case_id, 'protocolado', number.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['cases-kanban'] });
      toast.success(`Protocolo ${number.trim()} registrado para ${caso.client_name}.`);
      onClose();
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Erro ao registrar o protocolo');
    },
  });

  function handleConfirm() {
    if (!number.trim() || protocolMutation.isPending) return;
    protocolMutation.mutate();
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: '1.125rem' }}>Registrar Protocolo</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: '.2rem .4rem' }}>
            <X size={16} />
          </button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '.875rem', marginBottom: '.75rem' }}>
            Informe o número do processo gerado pelo CEJUSC/PJe:
          </p>
          <div className="form-group">
            <label>Número do processo</label>
            <input
              type="text"
              value={number}
              onChange={e => setNumber(e.target.value)}
              placeholder="0000000-00.0000.0.00.0000"
              className="mono-input"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleConfirm()}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancelar</button>
          <button
            className="btn btn-primary btn-sm"
            style={{ background: 'var(--success)', borderColor: 'var(--success)' }}
            onClick={handleConfirm}
            disabled={!number.trim() || protocolMutation.isPending}
          >
            {protocolMutation.isPending
              ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Registrando...</>
              : <><Hash size={13} /> Registrar</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function CaseModal({ caso, onClose, onArchive, canArchive }) {
  const navigate = useNavigate();
  const [showProtocol, setShowProtocol] = useState(false);
  const stale = isStale(caso);
  const issueLabel = generationIssueLabel(caso);

  function getActionButton() {
    switch (caso.status) {
      case 'aguardando_revisao':
        return (
          <button className="btn btn-gold" onClick={() => { onClose(); navigate('/revisao', { state: { caseId: caso.case_id } }); }}>
            <FileText size={14} /> Revisar Petição
          </button>
        );
      case 'aguardando_aprovacao':
        return (
          <button className="btn btn-primary" style={{ background: 'var(--success)', borderColor: 'var(--success)' }}
            onClick={() => { onClose(); navigate('/aprovacao', { state: { caseId: caso.case_id } }); }}>
            <CheckCircle size={14} /> Aprovar
          </button>
        );
      case 'aguardando_protocolo':
        return (
          <button className="btn btn-primary" onClick={() => setShowProtocol(true)}>
            <Hash size={14} /> Registrar Protocolo
          </button>
        );
      default:
        return null;
    }
  }

  const actionBtn = getActionButton();

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <div>
              <h3 style={{ fontSize: '1.125rem', marginBottom: '.2rem' }}>{caso.client_name || 'Sem nome'}</h3>
              <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                <span className="badge badge-navy" style={{ fontSize: '.65rem' }}>{caso.comarca}</span>
                {caso.status === 'gerando_peticao' && (
                  <span className="badge pulsing-badge" style={{ fontSize: '.65rem', background: 'var(--info-bg)', color: 'var(--info)', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                    <span className="spinner-mini" style={{ borderTopColor: 'var(--info)' }} /> Gerando Petição
                  </span>
                )}
                {stale && <span className="badge badge-error" style={{ fontSize: '.65rem' }}>Parado</span>}
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: '.2rem .4rem', marginLeft: 'auto' }}>
              <X size={16} />
            </button>
          </div>

          <div className="modal-body">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              {caso.status === 'gerando_peticao' && (
                <div className="alert alert-info pulsing-badge" style={{ marginBottom: '.25rem', padding: '.4rem .6rem', fontSize: '.775rem' }}>
                  <Zap size={13} style={{ color: 'var(--info)' }} />
                  <span>Petição sendo gerada com IA. Isso leva cerca de 1 a 3 minutos.</span>
                </div>
              )}
              {issueLabel && (
                <div className="alert alert-warning" style={{ marginBottom: '.25rem', padding: '.5rem .65rem', fontSize: '.775rem' }}>
                  <AlertTriangle size={13} />
                  <span>
                    <strong>{issueLabel}.</strong> {caso.generation_error_message}
                    {caso.quality_criticals?.length ? ` Pendências: ${caso.quality_criticals.join('; ')}.` : ''}
                  </span>
                </div>
              )}
              <ModalRow icon={<Building2 size={13} />} label="Reclamado" value={caso.defendant_name} />
              <ModalRow icon={<MapPin size={13} />} label="Comarca" value={caso.comarca} />
              {caso.hire_date && (
                <ModalRow label="Admissão" value={formatDate(caso.hire_date)} />
              )}
              {caso.termination_date && (
                <ModalRow label="Demissão" value={formatDate(caso.termination_date)} />
              )}
              {caso.atendente_name && (
                <ModalRow icon={<User size={13} />} label="Atendente" value={caso.atendente_name} />
              )}
              {caso.case_number && (
                <ModalRow label="Nº do processo" value={caso.case_number} mono />
              )}
              {caso.updated_at && (
                <ModalRow
                  icon={<Clock size={13} />}
                  label="Última atualização"
                  value={formatDistanceToNow(new Date(caso.updated_at), { addSuffix: true, locale: ptBR })}
                />
              )}
              {caso.form_completeness != null && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.25rem' }}>
                    <span style={{ fontSize: '.775rem', color: 'var(--text-muted)' }}>Completude da ficha</span>
                    <span style={{ fontSize: '.775rem', fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                      {caso.form_completeness}%
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        '--fill': caso.form_completeness / 100,
                        background: caso.form_completeness >= 80 ? 'var(--success)' : caso.form_completeness >= 50 ? 'var(--warning)' : 'var(--error)',
                      }}
                    />
                  </div>
                </div>
              )}

              {caso.petition_id && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem', marginTop: '.75rem', paddingTop: '.75rem', borderTop: '1px solid var(--border-light)' }}>
                  <span style={{ fontSize: '.775rem', color: 'var(--text-muted)' }}>Downloads da petição (vigente):</span>
                  <div style={{ display: 'flex', gap: '.5rem' }}>
                    <button className="btn btn-outline btn-sm" style={{ flex: 1 }} onClick={async () => {
                      try {
                        const blob = await petitionsApi.download(caso.petition_id, 'docx');
                        triggerBlobDownload(blob, `peticao_v${caso.petition_version || 1}_${(caso.client_name || 'cliente').toLowerCase().replace(/[^a-z0-9]+/g, '_')}.docx`);
                        toast.success('Download .docx iniciado');
                      } catch {
                        toast.error('Erro ao baixar');
                      }
                    }}>
                      Baixar .docx
                    </button>
                    <button className="btn btn-outline btn-sm" style={{ flex: 1 }} onClick={async () => {
                      try {
                        const blob = await petitionsApi.download(caso.petition_id, 'md');
                        triggerBlobDownload(blob, `peticao_v${caso.petition_version || 1}_${(caso.client_name || 'cliente').toLowerCase().replace(/[^a-z0-9]+/g, '_')}.md`);
                        toast.success('Download .md iniciado');
                      } catch {
                        toast.error('Erro ao baixar');
                      }
                    }}>
                      Baixar .md
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer" style={{ flexWrap: 'wrap' }}>
            <button className="btn btn-outline btn-sm" onClick={() => { onClose(); navigate(`/ficha/${caso.case_id}`); }}>
              <FileText size={13} /> Editar Ficha
            </button>
            {canArchive && (
              <button className="btn btn-ghost btn-sm" onClick={() => onArchive(caso)}>
                {caso.archived_at ? <RotateCcw size={13} /> : <Archive size={13} />}
                {caso.archived_at ? 'Desarquivar' : 'Arquivar'}
              </button>
            )}
            {actionBtn}
          </div>
        </div>
      </div>

      {showProtocol && (
        <ProtocolModal
          caso={caso}
          onClose={() => { setShowProtocol(false); onClose(); }}
        />
      )}
    </>
  );
}

function ModalRow({ icon, label, value, mono }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '.5rem' }}>
      <span style={{ fontSize: '.775rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '.25rem', flexShrink: 0 }}>
        {icon} {label}
      </span>
      <span style={{ fontSize: '.775rem', color: 'var(--text-primary)', fontWeight: 500, textAlign: 'right', fontFamily: mono ? 'var(--font-mono)' : undefined }}>
        {value || '—'}
      </span>
    </div>
  );
}

export default function Kanban() {
  const [modalCase, setModalCase] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState({ key: 'updated_at', dir: 'desc' });
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const canArchive = user?.permissions?.includes(PERMISSIONS.CASE_ARCHIVE);

  const view = searchParams.get('view') || 'kanban';
  const filters = {
    q: searchParams.get('q') || undefined,
    status: searchParams.get('status') || undefined,
    atendente: searchParams.get('atendente') || undefined,
    comarca: searchParams.get('comarca') || undefined,
    created_from: searchParams.get('created_from') || undefined,
    created_to: searchParams.get('created_to') || undefined,
    archived: searchParams.get('archived') === 'true' || undefined,
  };

  function setParam(key, value) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  }

  const { data: cases = [], isLoading } = useQuery({
    queryKey: ['cases', filters],
    queryFn:  () => casesApi.list(filters),
    refetchInterval: (query) => {
      const hasGenerating = query?.state?.data?.some(c => c.status === 'gerando_peticao');
      return hasGenerating ? 5000 : 30000;
    },
  });
  const { data: filterCases = [] } = useQuery({
    queryKey: ['cases-filter-options', !!filters.archived],
    queryFn: () => casesApi.list(filters.archived ? { archived: true } : {}),
    staleTime: 60000,
  });

  const archiveMutation = useMutation({
    mutationFn: (caso) => caso.archived_at ? casesApi.unarchive(caso.case_id) : casesApi.archive(caso.case_id),
    onSuccess: (_data, caso) => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      toast.success(caso.archived_at ? 'Caso desarquivado.' : 'Caso arquivado.');
      setModalCase(null);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Erro ao arquivar caso'),
  });

  function handleArchive(caso) {
    const action = caso.archived_at ? 'desarquivar' : 'arquivar';
    if (window.confirm(`Confirmar ${action} este caso?`)) archiveMutation.mutate(caso);
  }

  const byStatus = {};
  COLS.forEach(c => { byStatus[c.key] = []; });
  cases.forEach(c => {
    if (byStatus[c.status]) byStatus[c.status].push(c);
    else byStatus['ficha_preenchimento'].push(c);
  });

  const total = cases.length;
  const atendentes = [...new Map(filterCases.filter(c => c.assigned_to && c.atendente_name).map(c => [c.assigned_to, c.atendente_name])).entries()];
  const sortedCases = [...cases].sort((a, b) => {
    const av = a[sort.key] || '';
    const bv = b[sort.key] || '';
    const cmp = String(av).localeCompare(String(bv), 'pt-BR', { numeric: true });
    return sort.dir === 'asc' ? cmp : -cmp;
  });
  function toggleSort(key) {
    setSort(s => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));
  }

  return (
    <div>
      {/* CTA "Nova Ficha" fica só no header global — evita dois botões gold na mesma tela */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1>Casos</h1>
        <p style={{ marginTop: '.1rem' }}>
          {total} caso{total !== 1 ? 's' : ''}{filters.archived ? ' arquivado(s)' : ' em operação'}
        </p>
      </div>

      <div className="card cases-filter-card">
        <div className="cases-filter-grid">
          <FGFilter label="Busca">
            <div style={{ position: 'relative' }}>
              <Search size={13} style={{ position: 'absolute', left: 9, top: 9, color: 'var(--text-muted)' }} />
              <input value={filters.q || ''} onChange={e => setParam('q', e.target.value)} placeholder="Cliente, CPF ou reclamado" style={{ paddingLeft: 30 }} />
            </div>
          </FGFilter>
          <FGFilter label="Status">
            <select value={filters.status || ''} onChange={e => setParam('status', e.target.value)}>
              <option value="">Todos</option>
              {COLS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </FGFilter>
          <FGFilter label="Atendente">
            <select value={filters.atendente || ''} onChange={e => setParam('atendente', e.target.value)}>
              <option value="">Todos</option>
              {atendentes.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </FGFilter>
          <FGFilter label="Comarca">
            <input value={filters.comarca || ''} onChange={e => setParam('comarca', e.target.value)} placeholder="BATATAIS" />
          </FGFilter>
          <FGFilter label="Criado de/até">
            <div className="cases-date-range">
              <input type="date" value={filters.created_from || ''} onChange={e => setParam('created_from', e.target.value)} />
              <input type="date" value={filters.created_to || ''} onChange={e => setParam('created_to', e.target.value)} />
            </div>
          </FGFilter>
          <div className="cases-filter-actions">
            <label className="check-group" style={{ whiteSpace: 'nowrap', fontSize: '.78rem' }}>
              <input type="checkbox" checked={!!filters.archived} onChange={e => setParam('archived', e.target.checked ? 'true' : '')} />
              <span className="check-label">Arquivadas</span>
            </label>
            <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <button className="btn btn-ghost btn-sm" style={{ borderRadius: 0, background: view === 'kanban' ? 'var(--bg-hover)' : 'transparent' }} onClick={() => setParam('view', 'kanban')} title="Kanban"><Columns3 size={13} /></button>
              <button className="btn btn-ghost btn-sm" style={{ borderRadius: 0, background: view === 'lista' ? 'var(--bg-hover)' : 'transparent' }} onClick={() => setParam('view', 'lista')} title="Lista"><List size={13} /></button>
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <div className="spinner" style={{ width: 28, height: 28 }} />
        </div>
      ) : view === 'lista' ? (
        <CasesList cases={sortedCases} onOpen={setModalCase} onArchive={handleArchive} canArchive={canArchive} sort={sort} onSort={toggleSort} />
      ) : (
        <div className="kanban-board">
          {COLS.map(col => (
            <div key={col.key} className="kanban-col">
              <div className="kanban-col-header" style={{ borderBottom: `2px solid ${col.color}` }}>
                <span className="kanban-col-label">{col.label}</span>
                <div className="kanban-col-count" style={{ background: col.color }}>
                  {byStatus[col.key]?.length || 0}
                </div>
              </div>
              {byStatus[col.key]?.length === 0
                ? <div className="kanban-empty">Vazio</div>
                : byStatus[col.key].map(c => (
                    <CaseCard key={c.case_id} caso={c} onClick={setModalCase} />
                  ))
              }
            </div>
          ))}
        </div>
      )}

      {modalCase && (
        <CaseModal caso={modalCase} onClose={() => setModalCase(null)} onArchive={handleArchive} canArchive={canArchive} />
      )}
    </div>
  );
}

function FGFilter({ label, children }) {
  return (
    <div className="form-group" style={{ margin: 0 }}>
      <label style={{ fontSize: '.68rem' }}>{label}</label>
      {children}
    </div>
  );
}

function SortHead({ children, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  return (
    <th style={{ padding: '.55rem .7rem', textAlign: 'left', fontSize: '.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', cursor: 'pointer' }} onClick={() => onSort(sortKey)}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.25rem' }}>{children} <ArrowUpDown size={11} style={{ opacity: active ? 1 : 0.35 }} /></span>
    </th>
  );
}

function CasesList({ cases, onOpen, onArchive, canArchive, sort, onSort }) {
  if (cases.length === 0) {
    return <div className="card" style={{ padding: '2rem', color: 'var(--text-muted)', textAlign: 'center' }}>Nenhum caso encontrado.</div>;
  }
  return (
    <div className="card" style={{ padding: 0, overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
            <SortHead sortKey="client_name" sort={sort} onSort={onSort}>Cliente</SortHead>
            <SortHead sortKey="defendant_name" sort={sort} onSort={onSort}>Reclamado</SortHead>
            <SortHead sortKey="comarca" sort={sort} onSort={onSort}>Comarca</SortHead>
            <SortHead sortKey="status" sort={sort} onSort={onSort}>Status</SortHead>
            <SortHead sortKey="petition_version" sort={sort} onSort={onSort}>Versão</SortHead>
            <SortHead sortKey="atendente_name" sort={sort} onSort={onSort}>Atendente</SortHead>
            <SortHead sortKey="updated_at" sort={sort} onSort={onSort}>Atualizado</SortHead>
            <th />
          </tr>
        </thead>
        <tbody>
          {cases.map(caso => (
            <tr key={caso.case_id} style={{ borderBottom: '1px solid var(--border-light)', background: caso.archived_at ? 'rgba(142,153,172,.08)' : 'transparent' }}>
              <td style={{ padding: '.65rem .7rem', fontWeight: 600 }}>{caso.client_name || 'Sem nome'}{caso.archived_at && <span className="badge badge-navy" style={{ marginLeft: '.35rem', fontSize: '.62rem' }}>Arquivada</span>}</td>
              <td style={{ padding: '.65rem .7rem' }}>{caso.defendant_name || '—'}</td>
              <td style={{ padding: '.65rem .7rem' }}>{caso.comarca || '—'}</td>
              <td style={{ padding: '.65rem .7rem' }}><span className="badge badge-gold">{COLS.find(c => c.key === caso.status)?.label || caso.status}</span></td>
              <td style={{ padding: '.65rem .7rem', fontFamily: 'var(--font-mono)' }}>{caso.petition_version || '—'}</td>
              <td style={{ padding: '.65rem .7rem' }}>{caso.atendente_name || '—'}</td>
              <td style={{ padding: '.65rem .7rem' }}>{caso.updated_at ? format(new Date(caso.updated_at), 'dd/MM/yy HH:mm', { locale: ptBR }) : '—'}</td>
              <td style={{ padding: '.65rem .7rem' }}>
                <div style={{ display: 'flex', gap: '.25rem', justifyContent: 'flex-end' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => onOpen(caso)} title="Abrir"><FileText size={13} /></button>
                  {canArchive && (
                    <button className="btn btn-ghost btn-sm" onClick={() => onArchive(caso)} title={caso.archived_at ? 'Desarquivar' : 'Arquivar'}>
                      {caso.archived_at ? <RotateCcw size={13} /> : <Archive size={13} />}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
